import * as React from "react";
import { useEffect, useState } from "react";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Battery60Icon from "@mui/icons-material/Battery60";
import EvStationIcon from "@mui/icons-material/EvStation";
import PedalBikeIcon from "@mui/icons-material/PedalBike";
import PersonIcon from "@mui/icons-material/Person";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import HomeIcon from "@mui/icons-material/Home";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import BadgeIcon from "@mui/icons-material/Badge";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MessageIcon from "@mui/icons-material/Message";
// import SubjectIcon from "@mui/icons-material/Subject";
import MemoryIcon from "@mui/icons-material/Memory";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import { type Navigation, type Session } from "@toolpad/core/AppProvider";
import { DashboardLayout, ThemeSwitcher } from "@toolpad/core/DashboardLayout";
import { Outlet } from "react-router-dom";
import { CircularProgress, Stack } from "@mui/material";
import { Account } from "@toolpad/core";
import { useIntl } from "react-intl";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import { useMsal } from "@azure/msal-react";

import theme from "../theme";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { loginRequest } from "../core/config";
import { AppClient } from "../services/client.service";
import { authService } from "../services/auth.service";
import { identityService } from "../services/identity.service";
import { Center } from "../components/Center";
import { useAxiosErrorInterceptor } from "../core/axios-interceptors";

import { enUS, itIT } from "@mui/x-data-grid/locales";
import { useI18n } from "../i18n-context";

const NAVIGATION = [
  {
    segment: "",
    title: "dashboard",
    icon: <DashboardIcon />,
  },
  {
    segment: "batteries",
    title: "batteries",
    icon: <Battery60Icon />,
    permission: {
      url: "batteries",
      method: "GET",
    },
    pattern: "batteries{/:id}{/:action}?",
  },
  {
    segment: "battery-firmwares",
    title: "batteryFirmwares",
    icon: <DeveloperBoardIcon />,
    permission: {
      url: "batteries/firmwares",
      method: "GET",
    },
    pattern: "battery-firmwares{/:id}{/:action}?",
  },
  {
    segment: "vehicle-firmwares",
    title: "vehicleFirmwares",
    icon: <MemoryIcon />,
    permission: {
      url: "vehicles/firmwares",
      method: "GET",
    },
    pattern: "vehicle-firmwares{/:id}{/:action}?",
  },
  {
    segment: "battery-stations",
    title: "batteryStations",
    icon: <EvStationIcon />,
    permission: {
      url: "battery-stations",
      method: "GET",
    },
    pattern: "battery-stations{/:id}{/:action}?",
  },

  {
    segment: "home-chargers",
    title: "homeChargers",
    icon: <HomeIcon />,
    permission: {
      url: "home-chargers",
      method: "GET",
    },
    pattern: "home-chargers{/:id}{/:action}?",
  },

  {
    segment: "vehicles",
    title: "vehicles",
    icon: <PedalBikeIcon />,
    permission: {
      url: "vehicles",
      method: "GET",
    },
    pattern: "vehicles{/:id}{/:action}?",
  },
  {
    segment: "swaps",
    title: "swaps",
    icon: <SwapHorizIcon />,
    permission: {
      url: "swaps",
      method: "GET",
    },
    pattern: "swaps{/:id}{/:action}?",
  },
  {
    segment: "messages",
    title: "messages",
    icon: <MessageIcon />,
    permission: {
      url: "notifications",
      method: "GET",
    },
    pattern: "messages{/:id}{/:action}?",
  },
  {
    segment: "app-users",
    title: "appUsers",
    icon: <PersonIcon />,
    permission: {
      url: "users/app",
      method: "GET",
    },
    pattern: "app-users{/:id}{/:action}?",
  },
  {
    segment: "tester",
    title: "testers",
    icon: <RecordVoiceOverIcon />,
    permission: {
      url: "users/app",
      method: "GET",
    },
    pattern: "app-users{/:id}{/:action}?",
  },
  {
    segment: "groups",
    title: "groups",
    icon: <WorkspacesIcon />,
    permission: {
      url: "groups",
      method: "GET",
    },
    pattern: "groups{/:id}{/:action}?",
  },
  {
    segment: "battery-subscriptions",
    title: "batterySubscriptions",
    icon: <ReceiptIcon />,
    permission: {
      url: "subscriptions/batteries",
      method: "GET",
    },
    pattern: "battery-subscriptions{/:id}{/:action}?",
  },
  {
    segment: "home-charger-subscriptions",
    title: "homeChargerSubscriptions",
    icon: <ReceiptIcon />,
    permission: {
      url: "subscriptions/home-chargers",
      method: "GET",
    },
    pattern: "home-charger-subscriptions{/:id}{/:action}?",
  },
  {
    kind: "header",
    title: "management",
  },
  {
    segment: "users",
    title: "backofficeUsers",
    icon: <ManageAccountsIcon />,
    permission: {
      url: "users/app/{id:guid}/tester",
      method: "PATCH",
    },
  },
  {
    segment: "roles",
    title: "roles",
    icon: <BadgeIcon />,
    permission: {
      url: "roles",
      method: "GET",
    },
    pattern: "roles{/:id}{/:action}?",
  },

  // {
  //   segment: "logs",
  //   title: "logs",
  //   icon: <SubjectIcon />,
  //   permission: {
  //     url: "logs",
  //     method: "GET",
  //   },
  //   pattern: "logs{/:id}{/:action}?",
  // },
];

function ToolbarActions() {
  return (
    <Stack direction="row" alignItems="center">
      <ThemeSwitcher />
      <LanguageSwitcher />
      <Account />
    </Stack>
  );
}

export default function AppShell(props: any) {
  useAxiosErrorInterceptor();
  const { setLocale, locale } = useI18n();
  const intl = useIntl();
  const { instance } = useMsal();

  const [navigation, setNavigation] = useState<Navigation>([]);
  const [session, setSession] = React.useState<Session | null>(null);

  const [loadingUser, setLoadinUser] = React.useState<boolean>(false);
  const [loadingAuth, setLoadingAuth] = React.useState<boolean>(false);

  useEffect(() => {
    getIdentity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    buildNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, identityService.endpointsAllowedValue]);

  const login = async () => {
    const msalLoginResponse = await instance.loginPopup(loginRequest());
    setLoadingAuth(true);
    authService.accessTokenValue = msalLoginResponse.accessToken;
    setTimeout(async () => {
      const appLoginResponse = await AppClient.getInstance().login(
        AppClient.version
      );
      authService.accessTokenValue = appLoginResponse.token ?? null;
      authService.refreshTokenValue = appLoginResponse.refreshToken ?? null;
      authService.emailValue = appLoginResponse.email ?? null;
      authService.userIdValue = appLoginResponse.userId ?? null;
      getIdentity();
    }, 100);
  };

  const getIdentity = () => {
    if (!authService.accessTokenValue) {
      return;
    }
    setLoadinUser(true);
    AppClient.getInstance()
      .me(AppClient.version)
      .then((user) => {
        identityService.userValue = user;
        // SET LANG
        setLocale((user?.userSettings?.language as any) ?? "en");
        setSession({
          user: {
            email: user.email,
            name: `${user.name} ${user.lastName}`,
          },
        });
        setLoadinUser(false);
      });

    setLoadingAuth(true);
    AppClient.getInstance()
      .authorizedEndpoints(AppClient.version)
      .then((ea) => {
        identityService.endpointsAllowedValue = ea;
        setLoadingAuth(false);
      });
  };

  const buildNav = () => {
    if (!identityService.endpointsAllowedValue) {
      setNavigation([]);
      return;
    }
    console.log("Buil navbar");
    const nav = NAVIGATION.filter(
      (n) =>
        !n.permission ||
        identityService.checkEndpointsAllowed(
          n.permission.url,
          n.permission.method
        )
    ).map((n) => ({
      ...n,
      title: intl.formatMessage({ id: n.title }),
    })) as Navigation;
    setNavigation(nav);
  };

  const authentication = React.useMemo(() => {
    return {
      signIn: () => {
        login();
      },
      signOut: () => {
        authService.logout();
        window.location.replace("/");
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const themeWithLocale = React.useMemo(() => {
    const l = locale === "it" ? itIT : enUS;
    return theme(l);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  return (
    <ReactRouterAppProvider
      navigation={navigation}
      branding={{
        logo: <img src="/logo.png" alt="Enyring logo" />,
        title: "",
        homeUrl: "/",
      }}
      theme={themeWithLocale!}
      authentication={authentication}
      session={session}
    >
      <DashboardLayout
        slots={{
          toolbarActions: loadingUser
            ? () => <CircularProgress size={20} />
            : ToolbarActions,
        }}
      >
        {loadingUser || loadingAuth ? (
          <Center>
            <CircularProgress size={60} />
          </Center>
        ) : (
          <Outlet />
        )}
      </DashboardLayout>
    </ReactRouterAppProvider>
  );
}
